import React from 'react';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import { ADDRESS_CEP_FIELD_LABELS, formatCepDisplay } from '../utils/addressCepCheck';
import type { UseAddressCepCheckResult } from '../hooks/useAddressCepCheck';

interface AddressCepNoticeProps {
  check: UseAddressCepCheckResult;
  /** Quando informado, mostra o botão "Usar endereço do CEP". */
  onApplyCep?: () => void;
  className?: string;
}

/** Aviso da conferência automática do endereço pelo CEP (não bloqueia nada). */
export const AddressCepNotice: React.FC<AddressCepNoticeProps> = ({ check, onApplyCep, className = '' }) => {
  const cep = formatCepDisplay(check.checkedZip);

  if (check.status === 'loading') {
    return (
      <p className={`flex items-center gap-1 text-xs text-slate-500 ${className}`}>
        <Loader2 size={13} className="animate-spin" /> Conferindo o endereço pelo CEP…
      </p>
    );
  }

  if (check.status === 'unavailable') {
    return (
      <p className={`flex items-start gap-1 text-xs text-slate-500 ${className}`}>
        <Info size={13} className="mt-0.5 shrink-0" />
        Não foi possível conferir o endereço pelo CEP agora.
      </p>
    );
  }

  if (check.status === 'notFound') {
    return (
      <p className={`flex items-start gap-1 text-xs text-amber-700 ${className}`}>
        <AlertTriangle size={13} className="mt-0.5 shrink-0" />
        CEP {cep} não encontrado nos Correios. Confira o número.
      </p>
    );
  }

  if (check.status !== 'ok' || check.divergences.length === 0) return null;

  return (
    <div className={`rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 ${className}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
        <div className="flex-1 space-y-1">
          <p className="font-medium">O endereço não confere com o CEP {cep}</p>
          <ul className="space-y-0.5 text-xs">
            {check.divergences.map(d => (
              <li key={d.field}>
                <span className="font-medium">{ADDRESS_CEP_FIELD_LABELS[d.field]}:</span>{' '}
                digitado &quot;{d.typed}&quot; · CEP indica &quot;{d.expected}&quot;
              </li>
            ))}
          </ul>
          {onApplyCep && (
            <button
              type="button"
              onClick={onApplyCep}
              className="mt-1 text-xs font-medium text-amber-900 underline hover:text-amber-950"
            >
              Usar endereço do CEP
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
